import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { ActionMenuProps } from './types';
import { Button } from '@v2/components/Button';
import { styles } from './styles';
import clsx from 'clsx';

const ActionMenu: React.FC<ActionMenuProps> = ({
  items,
  ariaLabel = 'Actions',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  if (items.length === 0) return null;

  return (
    <div className={styles.wrapper} ref={menuRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        icon={<FontAwesomeIcon icon={faEllipsisV} />}
      />
      {isOpen && (
        <div className={styles.menu} role="menu">
          {items.map((item) => (
            <button
              key={item.key}
              role="menuitem"
              className={clsx(
                item.variant === 'danger' ? styles.itemDanger : styles.item,
                item.disabled && styles.itemDisabled
              )}
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return;
                item.onClick();
                handleClose();
              }}
            >
              {item.icon && <span className={styles.itemIcon}>{item.icon}</span>}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionMenu;
export { ActionMenu };
export type { ActionMenuProps, ActionMenuItem } from './types';
